import React, {
    createContext,
    useState
} from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router";
import {
    QueryClient,
    QueryClientProvider,
    useQuery
  } from '@tanstack/react-query'
import { ProtectedRoute } from "./protected-route";
import { PushNotification } from './push-notification'

// Used for push notifications
export const NotificationContext = createContext(null);
export const handleError = (err, setNotification) => {
  setNotification({
    message : 'An error has occured.',
    error_message : err
  })
  console.log('Error caught in handleError: ', err);
}

import * as CONSTANTS from "./constants";
import * as QUERIES from "./query-functions";

import BookOnline from "./book-online/book-online";
import SelectSeats from "./book-online/select-seats";
import EditTickets from './edit-tickets/edit-tickets';
import Extras from "./extras/extras";
import SwitchToIframes from "./components/switch-to-iframes";
import TicketDonations from "./ticket-donations/ticket-donations";
import IframeBasket from "./basket/iframe-basket";
import Basket from "./basket/basket";
import Login from "./login/login";
import Checkout from "./checkout/checkout";
import CreateAccount from "./account/create-account";
import OrderConfirmation from "./order-confirmation/order-confirmation"
import Account from './account/account'
import AccountOrders from './account/account-orders'
import AccountTickets from "./account/account-tickets";
import AccountMemberships from './account/account-memberships'
import AccountHelp from './account/account-help'
import AccountPreferences from './account/account-preferences'
import AccountDetails from './account/account-details'
import AccountPayments from './account/account-payments'
import IframeAccount from "./account/iframe-account";

const queryClient = new QueryClient();

// These queries are used in a lot of files so they are called from this root file.
export const getBasketData = () => {
    return useQuery({
        queryKey: ['basket'],
        queryFn: QUERIES.getBasket,
        staleTime: 86400000,
    });
}
export const refetchBasketData = () => {
    queryClient.invalidateQueries(['basket']);
};

export const getCustomer = () => {
    return useQuery({
        queryKey: ['customerData'],
        queryFn: QUERIES.getCustomer,
        staleTime: 86400000,
        retry: false
    });
}
export const refetchCustomer = () => {
    queryClient.invalidateQueries(['customerData']);
};

const App = () => {
    const [notification, setNotification] = useState(null);

    return (
      <div className="booking-pathway">
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <NotificationContext.Provider value={{ notification, setNotification }}>
              <Routes>
                
                {/* Event routes */}
                <Route path="book-online/:instanceId?" element={<BookOnline />} />
                <Route path="book-online/:instanceId?/select-seats" element={<SelectSeats />} />
                <Route path="book-online/:instanceId?/edit-tickets" element={<EditTickets />} />
                <Route path="book-online/:instanceId?/extras" element={<Extras />} />
                {CONSTANTS.HAS_TICKET_DONATIONS &&
                  <Route path="book-online/:instanceId?/ticket-donations" element={<TicketDonations />} />
                }
                {CONSTANTS.USE_IFRAMES_FROM &&
                  <Route path="book-online/:instanceId?/next" element={<SwitchToIframes />} />
                }

                {/* Payment routes */}
                {CONSTANTS.USE_IFRAME_BASKET ?
                  <Route path="basket" element={<IframeBasket />} />
                :
                  <Route path="basket" element={<Basket />} />
                }
                <Route path="checkout" element={<Checkout />} />
                <Route path="order-confirmation" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />

                {/* Account routes */}
                {CONSTANTS.USE_IFRAME_ACCOUNT ?
                  <>
                    <Route path="login" element={<IframeAccount />} />
                    <Route path="account" element={<IframeAccount />} />
                  </>
                :
                  <>
                    <Route path="login" element={<Login />} />
                    <Route path="create-account" element={<CreateAccount />} />
                    <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                    <Route path="account/orders/" element={<ProtectedRoute><AccountOrders /></ProtectedRoute>} />
                    <Route path="account/orders/tickets/" element={<ProtectedRoute><AccountTickets /></ProtectedRoute>} />
                    <Route path="account/memberships" element={<ProtectedRoute><AccountMemberships /></ProtectedRoute>} />
                    <Route path="account/help" element={<ProtectedRoute><AccountHelp /></ProtectedRoute>} />
                    <Route path="account/preferences" element={<ProtectedRoute><AccountPreferences /></ProtectedRoute>} />
                    <Route path="account/details" element={<ProtectedRoute><AccountDetails /></ProtectedRoute>} />
                    <Route path="account/payments" element={<ProtectedRoute><AccountPayments /></ProtectedRoute>} />
                  </>
                }

              </Routes>
              <PushNotification />
            </NotificationContext.Provider>
          </QueryClientProvider>
        </BrowserRouter>
      </div>
    );
};

if (CONSTANTS.rootNode) {
    ReactDOM.createRoot(CONSTANTS.rootNode).render(<App />);
}